/**
 * ColorPicker Component
 *
 * A hex color picker with visual preview and manual input.
 * Used in the Branding Form Builder for selecting brand colors.
 */

import { useState, useCallback } from "react";

import type { ColorPickerProps } from "./types";
import { isValidHexColor } from "./types";

/**
 * ColorPicker - Hex color selection with preview
 *
 * CSS Classes:
 * - color-picker: Container element
 * - color-picker__label: Label text
 * - color-picker__required: Required indicator
 * - color-picker__input-group: Input and preview container
 * - color-picker__preview: Color preview swatch
 * - color-picker__input: Text input for hex value
 * - color-picker__input--error: Error state styling
 * - color-picker__native: Native color input (hidden)
 * - color-picker__error: Error message text
 */
export function ColorPicker({
  label,
  value,
  onChange,
  readOnly = false,
  required = false,
}: ColorPickerProps): React.JSX.Element {
  const [localValue, setLocalValue] = useState(value);
  const [hasError, setHasError] = useState(false);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);

      // Only propagate valid hex colors
      if (isValidHexColor(newValue)) {
        setHasError(false);
        onChange(newValue);
      } else if (newValue.length === 7) {
        // Full length but invalid
        setHasError(true);
      } else {
        setHasError(false);
      }
    },
    [onChange]
  );

  const handleNativeColorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value.toUpperCase();
      setLocalValue(newValue);
      setHasError(false);
      onChange(newValue);
    },
    [onChange]
  );

  const handleBlur = useCallback(() => {
    // On blur, validate and reset if invalid
    if (!isValidHexColor(localValue)) {
      setLocalValue(value);
      setHasError(false);
    }
  }, [localValue, value]);

  // Sync local value with prop changes
  if (value !== localValue && isValidHexColor(value) && !hasError) {
    setLocalValue(value);
  }

  const previewColor = isValidHexColor(localValue) ? localValue : value;

  return (
    <div className="color-picker flex flex-col gap-1.5">
      <label className="color-picker__label text-sm font-medium text-gray-700">
        {label}
        {required && <span className="color-picker__required ml-1 text-red-500">*</span>}
      </label>

      <div className="color-picker__input-group flex items-center gap-2">
        {/* Color preview swatch */}
        <div
          className="color-picker__preview relative h-9 w-9 cursor-pointer overflow-hidden rounded-md border border-gray-300 shadow-sm"
          style={{ backgroundColor: previewColor }}
        >
          {/* Native color picker overlay */}
          {!readOnly && (
            <input
              type="color"
              value={previewColor}
              onChange={handleNativeColorChange}
              className="color-picker__native absolute inset-0 h-full w-full cursor-pointer opacity-0"
              aria-label={`Color picker for ${label}`}
            />
          )}
        </div>

        {/* Hex input */}
        <input
          type="text"
          value={localValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          readOnly={readOnly}
          placeholder="#000000"
          maxLength={7}
          className={`color-picker__input h-9 w-24 rounded-md border px-2 font-mono text-sm uppercase ${
            hasError
              ? "color-picker__input--error border-red-300 focus:border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          } ${readOnly ? "cursor-not-allowed bg-gray-50" : ""}`}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${label}-error` : undefined}
        />
      </div>

      {hasError && (
        <p id={`${label}-error`} className="color-picker__error text-xs text-red-500">
          Invalid hex color format (e.g., #3B82F6)
        </p>
      )}
    </div>
  );
}

export type { ColorPickerProps };
